// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {ClientApplication, Modules, Permissions, WebClientType} from "@models";
import {Translations, Translations as T} from "@translations";
import {SimUserExtendedService} from "./sim-user.service";
import {SimDeviceMappingService} from "./sim-device-mapping.service";
import {SimConfigurationService} from "./sim-configuration.service";
import {SimWebpagePermissionService} from "./sim-webpage-permission.service";
import {environment} from "../../../../environments/environment";

type DeepPartial<T> = {
	[K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export interface IPermissionOverrule {
	webClientTypes?: WebClientType[];
	permissions?: Permissions[];
	isExternal?: boolean;
}

export abstract class RoutePermission<T extends RoutePermission<T> = RoutePermission<any>> {
	routeLink: string;
	children?: T[];
	//Prerequisites
	permission?: Permissions;
	licenseModule?: Modules;
	webClientTypes?: WebClientType[];
	//Should be used to determine if the application is only available for network internal use, and not over a proxy
	internalOnly: boolean = false;

	abstract get name(): string;

	public hasAllPrerequisites(userService: SimUserExtendedService, overrule?: IPermissionOverrule): boolean {
		return this.hasSystemPrerequisites() && this.hasUserPrerequisites(userService, overrule) && this.hasClientPrerequisites(overrule);
	}

	//permission, roles
	public hasUserPrerequisites(userService: SimUserExtendedService, overrule?: IPermissionOverrule): boolean {
		let checkForPermission = true;
		if (this.permission) {
			checkForPermission = userService.hasPermission(this.permission, overrule?.permissions);
		}

		return checkForPermission && (!this.children?.length || this.children?.some((x) => x.hasUserPrerequisites(userService, overrule)));
	}

	//webClientTypes
	public hasClientPrerequisites(overrule?: IPermissionOverrule): boolean {
		let checkForDeviceAbility = true;
		if (this.webClientTypes && this.webClientTypes?.length >= 1) {
			let prop = overrule?.webClientTypes ?? SimDeviceMappingService.clientProperties().webClientTypes;
			checkForDeviceAbility = this.webClientTypes.some((value) => prop.includes(value));
		}

		if (this.internalOnly && (overrule?.isExternal ?? SimDeviceMappingService.clientProperties().isExternal)) {
			checkForDeviceAbility = false;
		}

		return checkForDeviceAbility && (!this.children?.length || this.children?.some((x) => x.hasClientPrerequisites(overrule)));
	}

	//licenseModule, scopes
	public hasSystemPrerequisites(): boolean {
		let hasLicense = true;
		if (this.licenseModule) {
			hasLicense = SimConfigurationService.hasLicenseModule(this.licenseModule);
		}

		return hasLicense && (!this.children?.length || this.children?.some((x) => x.hasSystemPrerequisites()));
	}
}

export class ClientApplicationTile extends RoutePermission<ClientApplicationTile> {
	id: ClientApplication;
	header: string;
	icon: string;
	iconBig: string;
	subHeader: string;
	isDisabled: boolean = false;

	public constructor(init?: DeepPartial<ClientApplicationTile>) {
		super();
		Object.assign(this, init);

		this.webClientTypes ??= [];
	}

	get name() {
		return ClientApplication[this.id];
	}

	public static parse(data: DeepPartial<ClientApplicationTile>[]): ClientApplicationTile[] {
		let ret: ClientApplicationTile[] = [];
		if (Array.isArray(data)) {
			for (const navItem of data) {
				ret.push(new ClientApplicationTile(navItem));
			}
		}
		return ret;
	}
}

export class MenuNavItem extends RoutePermission<MenuNavItem> {
	icon: string;
	name: string;

	public constructor(init?: DeepPartial<MenuNavItem>) {
		super();
		Object.assign(this, init);
		this.children = MenuNavItem.parse(init?.children) ?? [];
	}

	public static parse(data: DeepPartial<MenuNavItem>[]): MenuNavItem[] {
		let ret: MenuNavItem[] = [];
		if (Array.isArray(data)) {
			for (const navItem of data) {
				ret.push(new MenuNavItem(navItem));
			}
		}
		return ret;
	}
}

@Injectable({
	providedIn: "root",
})
export class SimMenuApptileService {
	public readonly ApplicationTilesMap: Map<ClientApplication, ClientApplicationTile> = new Map();
	public readonly ApplicationTiles: ClientApplicationTile[] = ClientApplicationTile.parse([
		{
			header: Translations.APP_TILE_TEAM_TRAINING,
			subHeader: Translations.APP_TILE_TEAM_TRAINING_SUBTITLE,
			icon: "trainingSmall",
			iconBig: "training",
			routeLink: "/session-selection/pro-session-selection",
			webClientTypes: [WebClientType.Recorder],
			id: ClientApplication.TeamTraining,
			internalOnly: true,
		},
		{
			header: Translations.APP_TILE_ARCHIVE,
			subHeader: Translations.APP_TILE_ARCHIVE_SUBTITLE,
			iconBig: "app_archive_big",
			icon: "app_archive",
			routeLink: "/web-manager/list/scenario-archive",
			id: ClientApplication.Archive,
			licenseModule: Modules.Archive,
		},
		{
			header: Translations.APP_TILE_EXAM_ARCHIVE,
			subHeader: Translations.APP_TILE_ARCHIVE_SUBTITLE,
			iconBig: "app_exam_archive_big",
			icon: "app_exam_archive",
			routeLink: "/web-manager/list/exam-archive",
			id: ClientApplication.ExamArchive,
			licenseModule: Modules.Exam,
		},
		{
			header: Translations.APP_TILE_CAM_VIEW,
			icon: "app_CamView",
			routeLink: "/camera-viewer/selection",
			webClientTypes: [WebClientType.Tablet, WebClientType.Recorder],
			id: ClientApplication.CamView,
			internalOnly: true,
		},
		{
			header: Translations.APP_TILE_PRESENTATION,
			icon: "app_Presentation",
			routeLink: "/presentation/selection",
			webClientTypes: [WebClientType.Tablet, WebClientType.Recorder],
			id: ClientApplication.Presentation,
			internalOnly: true,
		},
		{
			header: Translations.APP_TILE_ROOM_CAST,
			icon: "app_goLive",
			routeLink: "/live-view/selection",
			webClientTypes: [WebClientType.Tablet],
			id: ClientApplication.RoomCast,
			internalOnly: true,
		},
		{
			header: Translations.APP_TILE_HEALTH_MONITOR,
			icon: "app_HealthMonitor",
			routeLink: "/health-monitor",
			id: ClientApplication.HealthMonitor,
		},
		{
			header: Translations.APP_TILE_BOOKMARKS,
			icon: "app_Bookmarks",
			routeLink: "/web-manager/list/scenario-libraries/bookmarks",
			id: ClientApplication.Bookmarks,
		},
		{
			header: Translations.APP_TILE_CHECKLISTS,
			icon: "app_Checklists",
			routeLink: "/web-manager/list/scenario-libraries/checklists",
			id: ClientApplication.Checklists,
			licenseModule: Modules.Checklists,
		},
		{
			header: Translations.APP_TILE_AUDIO_TO_ROOM,
			icon: "app_AudioToRoom",
			routeLink: "/web-manager/list/scenario-libraries/vog-sets",
			id: ClientApplication.AudioToRoom,
		},
		{
			header: Translations.APP_TILE_AUDIO_OF_PATIENT,
			icon: "app_AudioOfPatient",
			routeLink: "/web-manager/list/scenario-libraries/vop-sets",
			id: ClientApplication.AudioOfPatient,
		},
		{
			header: Translations.APP_TILE_SCENARIO_MEDIAS,
			icon: "app_ScenarioMedia",
			routeLink: "/web-manager/list/scenario-libraries/scenario-medias",
			id: ClientApplication.ScenarioMedias,
		},
		{
			header: Translations.APP_TILE_SCENARIO_DESIGNER,
			iconBig: "app_scenarioDesigner_big",
			icon: "app_scenrioDesigner",
			routeLink: "/web-manager/list/scenario-editor",
			id: ClientApplication.ScenarioDesigner,
			licenseModule: Modules.ScenarioDesigner,
		},
		{
			header: Translations.APP_TILE_EVENT_PLANNER,
			iconBig: "app_EventPlanner_big",
			icon: "app_EventPlanner",
			routeLink: "/web-manager/list/event-manager",
			id: ClientApplication.EventPlanner,
			licenseModule: Modules.EventDesigner,
		},
		{
			header: Translations.APP_TILE_DEBRIEFING,
			subHeader: Translations.APP_TILE_DEBRIEFING_SUB_HEADER,
			iconBig: "app_Debriefing_big",
			icon: "app_debriefing",
			webClientTypes: [WebClientType.Tablet, WebClientType.Recorder],
			routeLink: "/debriefing/recording-selection",
			id: ClientApplication.Debriefing,
			internalOnly: true,
		},
		{
			header: Translations.APP_TILE_STAGE_DESIGNER,
			iconBig: "app_StageDesigner_big",
			icon: "app_StageDesigner",
			routeLink: "/stage-designer",
			id: ClientApplication.StageDesigner,
			internalOnly: true,
		},
		{
			header: Translations.APP_TILE_USER_MANAGEMENT,
			icon: "app_userManagement",
			routeLink: "/web-manager/list/user-manager",
			id: ClientApplication.UserManagement,
			licenseModule: Modules.UserManagment,
		},
		{
			header: Translations.APP_TILE_ROOM_PLANER,
			icon: "app_RoomManagement",
			routeLink: "/room-planner",
			id: ClientApplication.RoomPlanner,
		},
		{
			header: Translations.APP_TILE_MEDIA_MANAGEMENT,
			icon: "app_MediaManagement",
			routeLink: "/web-manager/list/media-management",
			id: ClientApplication.MediaManagement,
		},
		{
			header: Translations.APP_TILE_SYNC_RECORDINGS_FOR_OFFLINE_DEBRIEFING,
			subHeader: Translations.APP_SUB_TILE_SYNC_RECORDINGS_FOR_OFFLINE_DEBRIEFING,
			iconBig: "app_sync_big",
			icon: "sync",
			routeLink: "/web-manager/list/sync-recordings",
			id: ClientApplication.SyncRecordings,
		},
		{
			header: Translations.APP_TILE_ASSESSMENTS,
			routeLink: "/web-manager/list/assessments/assessments",
			icon: "app_Assessments",
			id: ClientApplication.Assessments,
			licenseModule: Modules.Assessments,
		},
		{
			header: Translations.APP_TILE_ASSESSMENTS_CONDUCTED,
			icon: "app_Assessments",
			routeLink: "/web-manager/list/assessments/user-recording-assessments",
			id: ClientApplication.AssessmentsConducted,
			licenseModule: Modules.Assessments,
		},
		{
			header: Translations.APP_TILE_DEVICE_REGISTRATION,
			icon: "app_DeviceRegistration",
			routeLink: "/device-registration",
			id: ClientApplication.DeviceRegistration,
			permission: Permissions.RegisterDevice,
		},
		{
			header: Translations.APP_TILE_EXAM_FLOW,
			icon: "app_exam_archive",
			routeLink: "/web-manager/list/exam-flow",
			id: ClientApplication.ExamFlow,
			licenseModule: Modules.Exam,
		},
		{
			header: Translations.APP_TILE_REMOTE_EXAMINATION,
			icon: "app_exam_archive",
			routeLink: "/web-manager/list/remote-examination",
			id: ClientApplication.RemoteExamination,
			licenseModule: Modules.Exam,
			webClientTypes: [WebClientType.ViewingStation],
		},
		{
			header: Translations.APP_TILE_STUDENT_APP,
			subHeader: Translations.APP_TILE_STUDENT_APP_SUBHEADER,
			iconBig: "app_PeerPracticeStudentApp_big",
			icon: "app_PeerPracticeStudentApp",
			routeLink: "/peer-practice-student-app",
			id: ClientApplication.PeerPracticeStudentApp,
			licenseModule: Modules.PeerPractice,
		},
		{
			header: Translations.APP_TILE_EXAM_DEVICE_MODE,
			iconBig: "app_PeerPracticeStudentApp_big",
			icon: "app_DeviceRegistration",
			routeLink: "/auto-login",
			id: ClientApplication.ExamDeviceMode,
			licenseModule: Modules.Exam,
		},
	]);
	public readonly MenuItems: MenuNavItem[] = MenuNavItem.parse([
		{
			icon: "fa-home fad",
			routeLink: "/event-dashboard",
			name: T.SIDE_MENU_DASHBOARD,
		},
		{
			icon: "fa-circle fad",
			routeLink: "/session-selection/pro-session-selection",
			name: T.SIDE_MENU_TEAM_TRAINING,
			webClientTypes: [WebClientType.Recorder],
			internalOnly: true,
		},
		{
			icon: "fa-user-chart fad",
			routeLink: "/debriefing/recording-selection",
			name: T.SIDE_MENU_DEBRIEFING,
			webClientTypes: [WebClientType.Tablet],
			internalOnly: true,
		},
		{
			icon: "fa-layer-plus fad",
			routeLink: "/stage-designer",
			name: T.SIDE_MENU_STAGE_DESIGNER,
			internalOnly: true,
		},
		{
			icon: "fad fa-door-open",
			routeLink: "/room-planner",
			name: T.SIDE_MENU_ROOM_PLANNER,
		},
		{
			icon: "fad fa-cctv",
			routeLink: "/camera-viewer/selection",
			name: T.SIDE_MENU_CAMERA_VIEWER,
			internalOnly: true,
		},
		{
			icon: "fad fa-presentation",
			routeLink: "/presentation/selection",
			name: T.APP_TILE_PRESENTATION,
			webClientTypes: [WebClientType.Tablet],
			internalOnly: true,
		},
		{
			icon: "fab fa-chromecast",
			name: Translations.APP_TILE_ROOM_CAST,
			routeLink: "/live-view/selection",
			webClientTypes: [WebClientType.Tablet],
			internalOnly: true,
		},
		{
			icon: "fad fa-phone-laptop",
			name: Translations.APP_TILE_DEVICE_REGISTRATION,
			routeLink: "/device-registration",
			permission: Permissions.RegisterDevice,
		},
		{
			icon: "fad fa-university",
			name: T.SIDE_MENU_EXAM,
			children: [
				{
					icon: "fa-folder-tree fad",
					routeLink: "/web-manager/list/exam-flow",
					name: Translations.APP_TILE_EXAM_FLOW,
					licenseModule: Modules.Exam,
				},
				{
					name: Translations.APP_TILE_REMOTE_EXAMINATION,
					icon: "fa-laptop-house fad",
					routeLink: "/web-manager/list/remote-examination",
					licenseModule: Modules.Exam,
					webClientTypes: [WebClientType.ViewingStation],
				},
				{
					icon: "fa-archive fad",
					routeLink: "/web-manager/list/exam-archive",
					name: T.SIDE_MENU_EXAM_ARCHIVE,
					licenseModule: Modules.Exam,
				},
				{
					icon: "fa-calendar-alt fad",
					routeLink: "/web-manager/list/exam-schedule",
					name: T.SIDE_MENU_EXAM_SCHEDULE,
					licenseModule: Modules.Exam,
				},
			],
		},
		{
			icon: "fa-object-group fad",
			name: Translations.SIDE_MENU_GROUP_MANAGER,
			permission: Permissions.GroupManager_Access,
			children: [
				{
					icon: "fa-users fad",
					routeLink: `/${environment.routes.groupManager}/users`,
					name: Translations.SIDE_MENU_GROUP_TYPE_USER,
				},
			],
		},
		{
			icon: "fa-file-chart-pie fad",
			name: T.REPORTS_TITLE,
			licenseModule: Modules.Monitoring,
			permission: Permissions.Reports_Access,
			children: [
				{
					icon: "fa-calendar fad",
					routeLink: "/web-manager/list/reports/session",
					name: T.REPORTS_SESSION_TITLE,
					licenseModule: Modules.Monitoring,
					permission: Permissions.Reports_Access,
				},
				{
					icon: "fa-file-chart-line far",
					routeLink: "/web-manager/list/reports/audio",
					name: T.REPORTS_AUDIO_TITLE,
					licenseModule: Modules.Monitoring,
					permission: Permissions.Grafana_Reports_Access,
				},
				{
					icon: "fa-file-chart-line far",
					routeLink: "/web-manager/list/reports/devices",
					name: T.REPORTS_DEVICES_TITLE,
					licenseModule: Modules.Monitoring,
					permission: Permissions.Grafana_Reports_Access,
				},
				{
					icon: "fa-file-chart-line far",
					routeLink: "/web-manager/list/reports/httpandconnections",
					name: T.REPORTS_HTTP_AND_CONNECTIONS_TITLE,
					licenseModule: Modules.Monitoring,
					permission: Permissions.Grafana_Reports_Access,
				},
				{
					icon: "fa-file-chart-line far",
					routeLink: "/web-manager/list/reports/blackbox",
					name: T.REPORTS_Blackbox_TITLE,
					licenseModule: Modules.Monitoring,
					permission: Permissions.Grafana_Reports_Access,
				},
				{
					icon: "fa-file-chart-line far",
					routeLink: "/web-manager/list/reports/windowsservers",
					name: T.REPORTS_WINDOWS_SERVERS_TITLE,
					licenseModule: Modules.Monitoring,
					permission: Permissions.Grafana_Reports_Access,
				},
			],
		},
		{
			icon: "fa-users-cog fad",
			name: T.SIDE_MENU_ADMINISTRATION,
			children: [
				{
					icon: "fa-archive fad",
					routeLink: "/web-manager/list/scenario-archive",
					name: T.SIDE_MENU_RECORDING_ARCHIVE,
					licenseModule: Modules.Archive,
				},
				{
					icon: "fad fa-sync",
					routeLink: "/web-manager/list/sync-recordings",
					name: T.APP_TILE_SYNC_RECORDINGS_FOR_OFFLINE_DEBRIEFING,
				},
				{
					icon: "fa-calendar fad",
					routeLink: "/web-manager/list/event-manager",
					name: T.SIDE_MENU_EVENT_PLANNER,
					licenseModule: Modules.EventDesigner,
				},
				{
					icon: "fa-cube fad",
					routeLink: "/web-manager/list/scenario-editor",
					name: T.SIDE_MENU_SCENARIO_DESIGNER,
					licenseModule: Modules.ScenarioDesigner,
				},
				{
					icon: "fa-users fad",
					routeLink: "/web-manager/list/user-manager",
					name: T.SIDE_MENU_USER_MANAGER,
					licenseModule: Modules.UserManagment,
				},

				{
					icon: "fa-sitemap fad",
					name: T.SIDE_MENU_SCENARIO_LIBRARIES,
					children: [
						{
							icon: "fa-bookmark fad",
							routeLink: "/web-manager/list/scenario-libraries/bookmarks",
							name: T.SIDE_MENU_BOOKMARKS,
						},
						{
							icon: "fad fa-clipboard-list-check",
							routeLink: "/web-manager/list/scenario-libraries/checklists",
							name: T.SIDE_MENU_CHECKLISTS,
							licenseModule: Modules.Checklists,
						},
						{
							icon: "fa-volume fad",
							routeLink: "/web-manager/list/scenario-libraries/vog-sets",
							name: T.SIDE_MENU_AUDIO_TO_ROOM,
							licenseModule: Modules.AudioToRoom,
						},
						{
							icon: "fa-lips fad",
							routeLink: "/web-manager/list/scenario-libraries/vop-sets",
							name: T.SIDE_MENU_VOICE_OF_PATIENT,
							licenseModule: Modules.AudioOfPatient,
						},
						{
							icon: "fa-photo-video fad",
							routeLink: "/web-manager/list/scenario-libraries/scenario-medias",
							name: T.SIDE_MENU_SCENARIO_MEDIA,
						},
					],
				},
				{
					icon: "fa-ballot-check fad",
					name: T.SIDE_MENU_ASSESSMENTS,
					licenseModule: Modules.Assessments,
					children: [
						{
							icon: "fa-ballot-check fad",
							routeLink: "/web-manager/list/assessments/assessments",
							name: T.SIDE_MENU_ASSESSMENT_TEMPLATES,
						},
						{
							icon: "fa-ballot-check fad",
							routeLink: "/web-manager/list/assessments/user-recording-assessments",
							name: T.SIDE_MENU_CONDUCTED_ASSESSMENTS,
						},
						{
							icon: "fad fa-cog",
							routeLink: "/web-manager/list/assessments/settings",
							name: T.SIDE_MENU_ASSESSMENT_SETTINGS,
						},
						{
							icon: "fad fa-smile",
							routeLink: "/web-manager/list/assessments/rating-item-icon-sets",
							name: T.SIDE_MENU_RATING_ITEM_ICON_SETS,
						},
					],
				},
				{
					icon: "fa-photo-video fad",
					routeLink: "/web-manager/list/media-management",
					name: T.SIDE_MENU_MEDIA_MANAGEMENT,
				},
				{
					icon: "fa-sign fad",
					routeLink: "/web-manager/list/digital-signage",
					name: T.SIDE_MENU_DIGITAL_SIGNAGE,
					licenseModule: Modules.DigitalSignature,
				},
				{
					icon: "fa-sliders-v fad",
					name: T.SIDE_MENU_SETTINGS,
					children: [
						{
							icon: "fa-cctv fad",
							routeLink: "/web-manager/list/admin-settings/camera-configuration",
							name: T.SIDE_MENU_CAMERA_CONFIGURATION,
						},
						{
							icon: "fad fa-door-open",
							routeLink: "/web-manager/list/admin-settings/room-configuration",
							name: T.SIDE_MENU_TRAINING_ROOM_CONFIGURATION,
						},
						{
							icon: "fad fa-th",
							routeLink: "/web-manager/list/admin-settings/training-room-groups",
							name: T.SIDE_MENU_TRAINING_ROOM_GROUPS,
						},
						{
							icon: "fa-phone-laptop fad",
							routeLink: "/web-manager/list/admin-settings/device-configuration",
							name: T.SIDE_MENU_DEVICE_CONFIGURATION,
						},
						{
							icon: "fad fa-th",
							routeLink: "/web-manager/list/admin-settings/scenario-groups",
							name: T.SIDE_MENU_SCENARIO_GROUPS,
						},
						{
							icon: "fad fa-microphone-alt",
							routeLink: "/web-manager/list/admin-settings/audio-input-channel-configuration",
							name: T.SIDE_MENU_AUDIO_INPUT_CHANNELS,
						},
						{
							icon: "fad fa-cog",
							routeLink: "/web-manager/list/admin-settings/audio-channel-groups-configuration",
							name: T.SIDE_MENU_AUDIO_CHANNEL_GROUP,
						},
						{
							icon: "fad fa-microphone-alt",
							routeLink: "/web-manager/list/admin-settings/network-audio-input-channel-configuration",
							name: T.SIDE_MENU_NETWORK_AUDIO_INPUT_CHANNELS,
						},
						{
							icon: "fad fa-phone",
							routeLink: "/web-manager/list/admin-settings/phone-audio-input-channel-configuration",
							name: T.SIDE_MENU_PHONE_AUDIO_INPUT_CHANNELS,
						},
						{
							icon: "fad fa-microphone-alt",
							routeLink: "/web-manager/list/admin-settings/audio-output-channel-configuration",
							name: T.SIDE_MENU_AUDIO_OUTPUT_CHANNELS,
						},
						{
							icon: "fad fa-user-alien",
							routeLink: "/web-manager/list/admin-settings/ldap-configuration",
							name: T.SIDE_MENU_LDAP_CONFIGURATION,
						},
						{
							icon: "fad fa-calendar",
							routeLink: "/web-manager/list/admin-settings/calendar-sync-configuration",
							name: T.SIDE_MENU_CALENDAR_SYNC_CONFIGURATION,
						},
						{
							icon: "fad fa-record-vinyl",
							routeLink: "/web-manager/list/admin-settings/recorder-mappings",
							name: T.SIDE_MENU_RECORDER_MAPPINGS,
						},
						{
							icon: "fad fa-record-vinyl",
							routeLink: "/web-manager/list/admin-settings/master-of-ceremony-mappings",
							name: "Master of Ceremony Mappings",
						},
						{
							icon: "fad fa-phone-laptop",
							routeLink: "/web-manager/list/admin-settings/player-mappings",
							name: T.SIDE_MENU_PLAYER_MAPPINGS,
						},
						{
							icon: "fad fa-tv-alt",
							routeLink: "/web-manager/list/admin-settings/viewing-station-mappings",
							name: T.SIDE_MENU_VIEWING_STATION_MAPPINGS,
						},
						{
							icon: "fad fa-project-diagram",
							routeLink: "/web-manager/list/admin-settings/trainingroom-pc-mappings",
							name: T.SIDE_MENU_TRAINING_ROOM_PC_MAPPINGS,
						},
						{
							icon: "fad fa-tablet",
							routeLink: "/web-manager/list/admin-settings/tablet-mappings",
							name: T.SIDE_MENU_TABLET_MAPPINGS,
						},
						{
							icon: "fad fa-desktop",
							routeLink: "/web-manager/list/admin-settings/ip-devices",
							name: T.SIDE_MENU_IP_DEVICES,
						},
						{
							icon: "fad fa-user-circle",
							routeLink: "/web-manager/list/admin-settings/sim-roles",
							name: T.SIDE_MENU_SIM_ROLES,
						},
						{
							icon: "fad fa-cog",
							routeLink: "/web-manager/list/admin-settings/exstreamers",
							name: T.SIDE_MENU_EXSTREAMERS,
						},
						{
							icon: "fad fa-database",
							routeLink: "/web-manager/list/admin-settings/edit-data-acl-settings",
							name: T.SIDE_MENU_DATA_ACL_CONFIGURATION,
						},
						{
							icon: "fa fa-database",
							routeLink: "/web-manager/list/admin-settings/edit-acl-settings",
							name: T.SIDE_MENU_ACL_CONFIGURATION,
						},
						{
							icon: "fad fa-language",
							routeLink: "/web-manager/list/admin-settings/translation-editor",
							name: T.SIDE_MENU_TRANSLATION_EDITOR,
						},
						{
							icon: "fad fa-window-maximize",
							routeLink: "/web-manager/list/admin-settings/control-panels",
							name: T.SIDE_MENU_CONTROL_PANELS,
						},
						{
							icon: "fad fa-browser",
							routeLink: "/web-manager/list/admin-settings/web-pages",
							name: T.SIDE_MENU_WEB_PAGES,
						},
						{
							icon: "fad fa-tools",
							routeLink: "/web-manager/list/admin-settings/general-settings-configuration",
							name: T.SIDE_MENU_GENERAL_SETTINGS,
						},
						{
							icon: "fa-file-certificate fad",
							routeLink: "/web-manager/list/sim-license-configuration",
							name: T.SIDE_MENU_SIM_LICENSE,
						},
						{
							icon: "fad fa-bell",
							routeLink: "/web-manager/list/admin-settings/notifications",
							name: T.SIDE_MENU_NOTIFICATION,
						},
						{
							icon: "fad fa-file",
							routeLink: "/web-manager/list/admin-settings/internal-streaming",
							name: T.SIDE_MENU_TRAIN_THE_TRAINER,
							licenseModule: Modules.TrainTheTrainer,
						},
						{
							icon: "fad fa-cog",
							routeLink: "/web-manager/list/admin-settings/synchronization-endpoints",
							name: T.SIDE_MENU_SYNCHRONIZATION_ENDPOINTS,
						},
						{
							icon: "fad fa-cog",
							routeLink: "/web-manager/list/admin-settings/report-configuration",
							name: T.SIDE_MENU_REPORT_CONFIGURATION,
							licenseModule: Modules.Monitoring,
						},
						{
							icon: "fad fa-cog",
							routeLink: "/web-manager/list/admin-settings/blackbox-exporter-configuration",
							name: T.SIDE_MENU_BLACKBOX_EXPORTER_CONFIGURATION,
							licenseModule: Modules.Monitoring,
						},
					],
				},
				{
					icon: "fa-history fad",
					routeLink: "/web-manager/list/connection-history",
					name: T.SIDE_MENU_CONNECTION_HISTORY,
				},
				{
					icon: "fa-clock fad",
					name: T.SIDE_MENU_SCHEDULERS_JOBS,
					children: [
						{
							icon: "fad fa-stopwatch",
							routeLink: "/web-manager/list/sim-schedulers-jobs/schedulers",
							name: T.SIDE_MENU_SCHEDULERS,
						},
						{
							icon: "fad fa-download",
							routeLink: "/web-manager/list/sim-schedulers-jobs/audio-video-jobs",
							name: T.SIDE_MENU_AUDIO_VIDEO_DOWNLOAD_JOBS,
						},
						{
							icon: "fad fa-download",
							routeLink: "/web-manager/list/sim-schedulers-jobs/export-jobs",
							name: T.SIDE_MENU_EXPORT_JOBS,
						},
						{
							icon: "fad fa-trash",
							routeLink: "/web-manager/list/sim-schedulers-jobs/delete-temp-files-jobs",
							name: T.SIDE_MENU_DELETE_TEMP_FILES_JOBS,
						},
						{
							icon: "fad fa-film",
							routeLink: "/web-manager/list/sim-schedulers-jobs/switchcast-video-upload-jobs",
							name: T.SIDE_MENU_SWITCHCAST_VIDEO_UPLOAD_JOBS,
						},
						{
							icon: "fad fa-film",
							routeLink: "/web-manager/list/sim-schedulers-jobs/switchcast-video-processing-jobs",
							name: T.SIDE_MENU_SWITCHCAST_VIDEO_PROCESSING_JOBS,
						},
						{
							icon: "fad fa-sync",
							routeLink: "/web-manager/list/sim-schedulers-jobs/synchronization-jobs",
							name: T.SIDE_MENU_SYNCHRONIZATION_JOBS,
						},
					],
				},
				{
					icon: "fad fa-question",
					name: T.SIDE_MENU_SUPPORT,
					children: [
						{
							icon: "fad fa-sitemap",
							routeLink: "/web-manager/list/support/invalid-scenarios",
							name: T.SIDE_MENU_INVALID_SCENARIOS,
						},
						{
							icon: "fad fa-door-open",
							routeLink: "/web-manager/list/support/invalid-training-rooms",
							name: T.SIDE_MENU_INVALID_TRAINING_ROOMS,
						},
					],
				},
				{
					icon: "fad fa-battery-bolt",
					name: T.SIDE_MENU_ENERGY_SAVING,
					licenseModule: Modules.EnergySaving,
					children: [
						{
							icon: "fad fa-outlet",
							name: T.ENERGY_SCHEDULES_EDIT_PAGE_PDUS,
							routeLink: "/web-manager/list/energy-saving/power-distribution-units",
						},
						{
							icon: "fad fa-calendar-alt",
							name: T.ENERGY_SCHEDULES_TITLE,
							routeLink: "/web-manager/list/energy-saving/energy-schedules",
						},
					],
				},
				{
					icon: "fad fa-solar-system",
					name: T.SIDE_MENU_SYSTEM_STATE,
					children: [
						{
							icon: "fad fa-phone-laptop",
							routeLink: "/web-manager/list/sim-system-state-overview/device-overview-graph",
							name: T.SIDE_MENU_DEVICE_OVERVIEW,
						},
						{
							icon: "fad fa-desktop",
							routeLink: "/web-manager/list/sim-system-state-overview/recorder-state",
							name: T.SIDE_MENU_RECORDER_STATE,
						},
						{
							icon: "fad fa-phone-laptop",
							routeLink: "/web-manager/list/sim-system-state-overview/player-state",
							name: T.SIDE_MENU_PLAYER_STATE,
						},
						{
							icon: "fad fa-tablet-alt",
							routeLink: "/web-manager/list/sim-system-state-overview/tablet-state",
							name: T.SIDE_MENU_TABLET_STATE,
						},
						{
							icon: "fad fa-sign-in",
							routeLink: "/web-manager/list/sim-system-state-overview/trainingroom-entrance-state",
							name: T.SIDE_MENU_TRAINING_ROOM_ENTRANCE_STATE,
						},
						{
							icon: "fad fa-eye",
							routeLink: "/web-manager/list/sim-system-state-overview/viewing-station-state",
							name: T.SIDE_MENU_VIEWING_STATION_STATE,
						},
						{
							icon: "fad fa-link",
							routeLink: "/web-manager/list/sim-system-state-overview/other-clients",
							name: T.SIDE_MENU_OTHER_CLIENTS,
						},
						{
							icon: "fad fa-video",
							routeLink: "/web-manager/list/sim-system-state-overview/active-recordings",
							name: T.SIDE_MENU_RUNNING_SCENARIO_RECORDINGS,
						},
						{
							icon: "fad fa-door-closed",
							routeLink: "/web-manager/list/sim-system-state-overview/occupied-trainingrooms",
							name: T.SIDE_MENU_OCCUPIED_TRAINING_ROOMS,
						},
					],
				},
				{
					icon: "fa-box-open fad",
					routeLink: "/web-manager/list/cache-manager",
					name: T.SIDE_MENU_CACHE_MANAGER,
				},
				{
					icon: "fa-list-alt fad",
					routeLink: "/web-manager/list/sim-audit-trail",
					name: T.SIDE_MENU_AUDIT_TRAIL,
				},
			],
		},
	]);

	constructor(
		private _simUserService: SimUserExtendedService,
		private _simWebpagePermissionService: SimWebpagePermissionService,
	) {
		for (const applicationTile of this.ApplicationTiles) {
			this.ApplicationTilesMap.set(applicationTile.id, applicationTile);
		}

		this.fillPermissions(this.ApplicationTiles);
		this.fillPermissions(this.MenuItems);
	}

	private fillPermissions(menuItems: RoutePermission[]) {
		if (menuItems?.length) {
			for (const menuItem of menuItems) {
				let associatedWebPage = this._simWebpagePermissionService.findWebPage(menuItem.routeLink, menuItem.name);
				if (associatedWebPage) {
					menuItem.permission = associatedWebPage.permissionCanViewPage;
				}
				this.fillPermissions(menuItem.children);
			}
		}
	}
}
